import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Search, FileText, MailOpen, LineChart, Plug,
  Quote, ArrowRight, Check, CircleCheck,
} from 'lucide-react';

/* ----------------------------------------------------------------------------
 * Marketing product page — neutral base + single signal-green accent,
 * editorial serif headings, hairline borders. Matches Home.js / the in-app
 * /proof screens so the marketing site and product feel like one system.
 * -------------------------------------------------------------------------- */

const GREEN = '#2563EB';

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
};

const features = [
  {
    category: 'Real-Work Screen',
    icon: Search,
    title: 'A Task That Mirrors the Job',
    description: 'Spin up a role-specific, AI-allowed assessment in minutes. Candidates do real, messy work the way they would on the job — not whiteboard trivia.',
    benefits: [
      'Templates for common eng roles',
      'AI explicitly allowed',
      'Author in under ten minutes',
    ],
  },
  {
    category: 'One Explainable Score',
    icon: FileText,
    title: 'Scored in Code, Not by a Model',
    description: 'A single 0–100 score is computed in code from your per-criterion rubric — never asserted by the model — with reasoning and evidence attached.',
    benefits: [
      'Per-criterion point breakdown',
      'Reasoning and evidence shown',
      'Resistant to prompt injection',
    ],
  },
  {
    category: 'Proof of Human',
    icon: MailOpen,
    title: 'A Real Person Behind the Work',
    description: 'Server-observed behavioral signals — paste vs. typed, timing, tab focus, environment — produce a plain proof-of-human state. No biometrics, ever.',
    benefits: [
      'Behavioral signals only',
      'Verified / needs review / flagged',
      'Server-attributed, never self-reported',
    ],
  },
  {
    category: 'Audit & Compliance',
    icon: LineChart,
    title: 'Explainable by Default',
    description: 'Every result carries the reasoning, evidence, and a full audit trail you can export — built with LL-144 and the Mobley v. Workday standard in mind.',
    benefits: [
      'Exportable audit trail',
      'Per-criterion evidence attached',
      'Logged from day one',
    ],
  },
];

const integrations = ['Greenhouse', 'Lever', 'Workable', 'Slack', 'Gmail', 'Outlook', 'Zoom'];

/* A small product-preview echoing the in-app UI, themed to the green system. */
function FeaturePreview({ index, accentBars }) {
  return (
    <div className="group relative aspect-video rounded-2xl border border-gray-200 dark:border-gray-800 bg-[#F8FAFC] dark:bg-gray-900 p-6 sm:p-8 flex items-center justify-center overflow-hidden">
      <div className="w-3/4 h-3/4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_24px_48px_-28px_rgba(15,23,42,0.16)] p-4 space-y-3 transition-transform duration-500 group-hover:-translate-y-1">
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-gray-200 dark:bg-gray-700" />
          <span className="h-2.5 w-2.5 rounded-full bg-gray-200 dark:bg-gray-700" />
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: '#BFDBFE' }} />
        </div>
        <div className="h-3.5 w-1/3 rounded bg-gray-200 dark:bg-gray-800" />
        <div className="space-y-2 pt-1">
          {accentBars.map((w, i) => (
            <div key={i} className="flex items-center gap-2">
              <motion.div
                className="h-2 rounded-full"
                style={{ background: i === 0 ? GREEN : '#E2E8F0' }}
                initial={{ width: 0 }}
                whileInView={{ width: w }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.1 * i + index * 0.05, ease: 'easeOut' }}
              />
              {i === 0 && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold" style={{ color: '#1D4FD8' }}>
                  <CircleCheck size={11} /> match
                </span>
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-2 pt-1">
          <div className="h-7 w-1/4 rounded-md" style={{ background: '#EFF4FE', border: '1px solid #BFDBFE' }} />
          <div className="h-7 w-1/4 rounded-md bg-gray-100 dark:bg-gray-800" />
        </div>
      </div>
    </div>
  );
}

function Product() {
  return (
    <div className="bg-[#F8FAFC] dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-white to-[#F8FAFC] dark:from-gray-950 dark:to-gray-950">
        <div className="mx-auto max-w-4xl px-6 py-20 text-center lg:px-8 lg:py-28">
          <motion.div {...reveal} transition={{ duration: 0.5 }} className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.05em] text-gray-500">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: GREEN }} />
            Real work. Real person. Provable.
          </motion.div>
          <motion.h1 {...reveal} transition={{ duration: 0.55, delay: 0.05 }}
            className="mt-6 font-serif text-5xl font-semibold leading-[1.05] tracking-tight text-gray-900 dark:text-white sm:text-6xl">
            One screen.<br />One trustworthy score.
          </motion.h1>
          <motion.p {...reveal} transition={{ duration: 0.55, delay: 0.1 }}
            className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-gray-600 dark:text-gray-300">
            Send a short, AI-allowed real-work screen. Get back one explainable score, tied to a verified human, with the reasoning, evidence, and audit trail attached.
          </motion.p>
          <motion.div {...reveal} transition={{ duration: 0.55, delay: 0.16 }} className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link to="/register" className="inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 text-base font-semibold text-white transition-colors"
              style={{ background: GREEN }} onMouseEnter={(e) => (e.currentTarget.style.background = '#1D4FD8')} onMouseLeave={(e) => (e.currentTarget.style.background = GREEN)}>
              Try it free
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-6 py-3 text-base font-semibold text-gray-900 dark:text-white transition-colors hover:border-gray-400">
              Become a design partner <ArrowRight size={17} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ===== FEATURE BLOCKS ===== */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
        <div className="flex flex-col gap-24">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            const accentBars = ['90%', '70%', '55%'];
            return (
              <motion.div
                key={index}
                {...reveal}
                transition={{ duration: 0.55, delay: 0.05 }}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12 lg:gap-16`}
              >
                {/* Text content */}
                <div className="flex-1 space-y-5">
                  <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold" style={{ background: '#EFF4FE', color: '#1D4FD8', border: '1px solid #BFDBFE' }}>
                    <Icon size={14} strokeWidth={1.9} />
                    {feature.category}
                  </div>
                  <h2 className="font-serif text-3xl font-semibold tracking-tight text-gray-900 dark:text-white sm:text-4xl">{feature.title}</h2>
                  <p className="text-lg leading-relaxed text-gray-600 dark:text-gray-300">
                    {feature.description}
                  </p>
                  <ul className="space-y-3 pt-1">
                    {feature.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-center gap-3 text-[15px] text-gray-700 dark:text-gray-200">
                        <Check size={17} style={{ color: GREEN }} className="flex-none" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Visual content */}
                <div className="w-full flex-1">
                  <FeaturePreview index={index} accentBars={accentBars} />
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ===== PRINCIPLES BAND ===== */}
      <section className="border-y border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2 lg:gap-16">
            <motion.div {...reveal} transition={{ duration: 0.5 }}>
              <Quote size={40} strokeWidth={1.6} style={{ color: GREEN }} className="mb-6" />
              <blockquote className="font-serif text-2xl font-medium leading-relaxed text-gray-900 dark:text-white">
                “A score you can't explain isn't a score you can trust. So ours is computed in code, shows its
                reasoning, and is tied to a verified human — every time.”
              </blockquote>
              <p className="mt-6 text-sm text-gray-500">The principle the whole product is built around.</p>
            </motion.div>

            <div className="grid grid-cols-2 gap-5">
              {[
                { value: 'In code', label: 'Scores computed, never asserted by the model' },
                { value: 'Verified', label: 'A real person behind every result' },
                { value: 'Explainable', label: 'Reasoning and evidence on every screen, exportable for audit' },
              ].map((r, i) => (
                <motion.div
                  key={r.label}
                  {...reveal}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className={`rounded-2xl border border-gray-200 dark:border-gray-800 bg-[#F8FAFC] dark:bg-gray-900 p-6 text-center ${i === 2 ? 'col-span-2' : ''}`}
                >
                  <div className="font-serif text-3xl font-semibold" style={{ color: GREEN }}>{r.value}</div>
                  <div className="mt-2 text-sm font-medium text-gray-600 dark:text-gray-300">{r.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== INTEGRATIONS ===== */}
      <section className="mx-auto max-w-7xl px-6 py-24 text-center lg:px-8">
        <motion.div {...reveal} transition={{ duration: 0.5 }} className="mx-auto max-w-2xl">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.06em]" style={{ color: GREEN }}>
            <Plug size={14} /> Integrations
          </div>
          <h2 className="mt-4 font-serif text-4xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Plays nice with your stack.
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">
            Connect AutoHire with your favorite ATS, communication tools, and calendars in one click.
          </p>
        </motion.div>

        <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
          {integrations.map((tool, i) => (
            <motion.div
              key={tool}
              {...reveal}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="whitespace-nowrap rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-6 py-3 text-base font-semibold text-gray-600 dark:text-gray-300 transition-colors hover:border-gray-300 dark:hover:border-gray-700"
            >
              {tool}
            </motion.div>
          ))}
        </div>
      </section>

      {/* ===== BOTTOM CTA ===== */}
      <section className="bg-[#0F172A]">
        <div className="mx-auto max-w-5xl px-6 py-24 text-center lg:px-8">
          <motion.div {...reveal} transition={{ duration: 0.55 }}>
            <h2 className="font-serif text-4xl font-semibold leading-tight text-white sm:text-5xl">
              Ready to hire on proof?
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-gray-300">
              We're onboarding a small group of startups hiring engineers as design partners. Run your real roles through it and help shape what this becomes.
            </p>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link to="/register" className="inline-flex items-center justify-center gap-2 rounded-lg px-7 py-3.5 text-base font-semibold text-white" style={{ background: GREEN }}>
                Try it free <ArrowRight size={17} />
              </Link>
              <Link to="/contact" className="inline-flex items-center justify-center rounded-lg border border-white/20 bg-transparent px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-white/10">
                Become a design partner
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default Product;
